import { computed, defineComponent, reactive, ref } from 'vue'
import {
  MAX_VISIBLE_ITEMS,
  type SearchDropdownProps,
  type SearchDropdownState
} from './types'
import { onClickOutside, useDebounceFn } from '@vueuse/core'
import ItemList from './components/item-list'
import type { IInputOption } from '../form/input/types'
import { toString } from '@/utils/formatting'
import { useMemoAsync } from '@/utils/async/useMemoAsync'
import { tv } from 'tailwind-variants'
import { useInterpolation } from '@/utils/animation/useInterpolation'
import { uniqueBy } from '@/utils/array'
import { FORM_INPUT_HEIGHT_PX } from '../form/constants'
import BasicInput from '../form/basic-input'
import { uref } from '@/utils/reactive/uref'

const clazz = tv({
  variants: {
    disabled: {
      true: 'opacity-[0.5]'
    }
  }
})

export const SearchDropDown = defineComponent<SearchDropdownProps>({
  setup(props) {
    const state = reactive<SearchDropdownState>({
      value: uref(props.value),
      expanded: props.expanded === true,
      addedItems: [],
      loading: false,
      searchString: undefined
    })

    const interpolation = useInterpolation({
      initial: 0,
      duration: 0.25
    })

    const setExpanded = (expanded: boolean) => {
      if (props.disabled) return
      if (state.expanded === expanded) return

      state.expanded = expanded

      if (expanded) {
        interpolation.runTo(1.0)
      } else {
        interpolation.runTo(0.0)
      }
    }

    const addItem = async (item: IInputOption) => {
      const fieldRef = props.fieldRef

      if (fieldRef) {
        if (fieldRef.value.options) {
          const oldItems = fieldRef.value.options.items || []
          fieldRef.value.options.items = uniqueBy([...oldItems, item], (it) => it.value)
        }

        if (props.reportChange) {
          props.reportChange(fieldRef, (item.value || '') as any)
        }
      }

      ;(state as any).addedItems = [...(state.addedItems as any[]), item]
    }

    const setLoading = (loading: boolean) => {
      state.loading = loading
    }

    const toggleExpanded = () => setExpanded(!state.expanded)

    const loadedItems = useMemoAsync<IInputOption[]>(async (): Promise<IInputOption[]> => {
      let items: IInputOption[] = props.options?.items || []
      const search = props.options?.search

      const fetchAll = search?.api?.fetchAll
      if ((!state.searchString || state.searchString.length <= 1) && fetchAll) {
        return await fetchAll()
      }

      if (
        state.searchString &&
        state.searchString.length > 1 &&
        state.searchString !== props.value
      ) {
        if (search && search.api && search.api.fetch) {
          return await search.api.fetch(state.searchString)
        }

        items = items.filter((it) => {
          const text = it.label || it.name || toString(it.value) || ''
          return text.toLowerCase().includes((state.searchString || '').toLowerCase())
        })
      }
      return items
    }, [() => state.searchString])

    const items = computed((): IInputOption[] => {
      if (loadedItems.loading) return []
      const items: IInputOption[] = [
        ...(state.addedItems as any[]),
        ...(loadedItems.data ? loadedItems.data : props.options?.items || [])
      ]

      return uniqueBy(items, (it) => it.value)
    })

    const selectedIndex = computed(() => {
      const value = props.value || state.value
      if (typeof value === 'undefined' || value === null) return -1
      return items.value.map((it) => it.value).indexOf(value)
    })

    /* const selectedItem = computed((): IInputOption | undefined => {
      const value = state.value
      if (typeof value === 'undefined' || value === null) return undefined
      return items.value.find(item => item.value === value)
      }) */

    const handleClickItem = (item: IInputOption) => {
      state.value = item
      searchText.value = item.label || ''

      if (props.onValueChange) {
        props.onValueChange(item.value);
      }

      const fieldRef = props.fieldRef
      if (fieldRef) {
        if (props.reportChange) {
          props.reportChange(fieldRef, item.value)
        }
      }

      if (item.action) {
        item.action(item);
      }

      toggleExpanded()
    }

    const handleClickSelectedItem = () => {
      toggleExpanded()
    }

    const performSearch = async (value: string) => {
      if (value && value.length > 1) {
        state.loading = true
        state.searchString = value
        state.loading = false
      }
    }

    const debouncedSearch = useDebounceFn(performSearch, 300)

    const handleSearchInput = (evt: Event) => {
      const target = evt.target as HTMLInputElement
      const value = target.value
      setExpanded(true)
      debouncedSearch(value)
    }

    const anchor = ref()
    const itemListRef = ref()

    onClickOutside(
      itemListRef,
      () => {
        if (!state.expanded) return
        setExpanded(false)
      },
      { ignore: [anchor] }
    )

    const maxVisibleItems = computed(() => {
      const maxVisible = props.options?.maxVisibleItems || MAX_VISIBLE_ITEMS
      return (
        Math.min(maxVisible, (items.value || []).length) +
        (props.options?.search?.emptyState ? 1 : 0)
      )
    })

    const searchText = ref('')

    return () => {
      return (
        <div
          class={clazz({ disabled: !!props.disabled })}
          style={{
            position: 'relative',
            width: '100%',
            height: FORM_INPUT_HEIGHT_PX + 'px',
            overflow: 'hidden',
            fontSize: '14px',
            ...(props.disabled ? { pointerEvents: 'none' } : {})
          }}
        >
          <BasicInput
            placeholder={props.placeholder}
            onClick={handleClickSelectedItem}
            ref={anchor}
            type="text"
            value={searchText.value}
            onInput={handleSearchInput}
          />
          <ItemList
            selectedIndex={selectedIndex.value}
            config={props.config}
            size={props.size}
            ref={itemListRef}
            onItemClick={handleClickItem}
            itemStyle={props.itemStyleExpanded}
            variant={props.variant}
            items={items.value || []}
            expanded={state.expanded}
            interpolation={interpolation.state.value}
            anchor={anchor}
            handleSearchInput={handleSearchInput}
            maxVisibleItems={maxVisibleItems.value}
            searchOptions={props.options?.search}
            state={state as any}
            disabled={!!props.disabled || !!props.locked}
            fieldRef={props.fieldRef}
            addItem={addItem}
            options={props.options}
            setLoading={setLoading}
            isLoading={state.loading || loadedItems.loading || false}
            minWidth={props.itemListMinWidth}
          />
        </div>
      )
    }
  }
})

SearchDropDown.props = [
  'noSelect',
  'value',
  'size',
  'options',
  'expanded',
  'hiddenWhenCollapsed',
  'fieldRef',
  'reportChange',
  'onInput',
  'type',
  'variant',
  'name',
  'locked',
  'disabled',
  'placeholder',
  'label',
  'errors',
  'onValueChange',
  'icon',
  'itemStyleExpanded',
  'itemStyleCollapsed',
  'itemListMinWidth',
  'small',
  'config'
]

export default SearchDropDown
